/**
 * Side effects that hydrate Redux state for the order tracking page:
 *  - Mirror public order tracking API data into Redux so order metadata
 *    persists across reloads (orderNumber, trackingCode, status, ETA).
 *  - Defensive cart clear when viewing a confirmed/preparing/ready/completed order
 *    (handles edge cases where finalizeOrder didn't fire).
 */

import { useEffect } from 'react'
import { useAppDispatch } from '@/store/hooks'
import { clearCart } from '@/store/cartSlice'
import {
  setEstimatedTime,
  setOrderNumber,
  setOrderStatus,
  setTrackingCode,
} from '@/store/orderSlice'
import type { OrderStatus } from '@/types/order'

interface PublicOrderDataLike {
  order_number?: string | null
  tracking_code?: string | null
  status?: string | null
  estimated_ready_at?: string | null
}

interface UseOrderHydrationArgs {
  publicOrderData: PublicOrderDataLike | undefined | null
  trackingCodeProp: string | undefined
  currentOrderStatus: OrderStatus
}

export function useOrderHydration({
  publicOrderData,
  trackingCodeProp,
  currentOrderStatus,
}: UseOrderHydrationArgs) {
  const dispatch = useAppDispatch()

  // Hydrate Redux from public API data — ensures orderNumber/trackingCode persist
  // to localStorage so reloads don't lose them and isPublicLink stays stable.
  useEffect(() => {
    if (!publicOrderData) return
    if (publicOrderData.order_number) {
      dispatch(setOrderNumber(publicOrderData.order_number))
    }
    if (publicOrderData.tracking_code || trackingCodeProp) {
      dispatch(setTrackingCode(publicOrderData.tracking_code || trackingCodeProp!))
    }
    if (publicOrderData.status) {
      dispatch(setOrderStatus(publicOrderData.status as OrderStatus))
    }
    if (publicOrderData.estimated_ready_at) {
      dispatch(setEstimatedTime(publicOrderData.estimated_ready_at))
    }
  }, [publicOrderData, trackingCodeProp, dispatch])

  // Safety net: clear cart when viewing a confirmed order (handles edge cases
  // where finalizeOrder didn't fire, e.g. page crash, stale closure, etc.)
  useEffect(() => {
    if (
      currentOrderStatus === 'confirmed' ||
      currentOrderStatus === 'preparing' ||
      currentOrderStatus === 'ready' ||
      currentOrderStatus === 'completed'
    ) {
      dispatch(clearCart())
    }
  }, [currentOrderStatus, dispatch])
}
